import { Formik } from 'formik';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import { success } from 'utils/toast';
import { addRole, fetchRoles } from 'services/user';
import CreateEditRole from './CreateEditRole';
import { handleError } from 'utils/errorHandler';
import { addRoleSchema } from 'schemas/addRoleSchema';
import Loading from 'components/common/loading/Loading';
import useEmployeeDebounceOptions from 'hooks/useEmployeeDebounceOptions';
import Modal from 'components/common/modal/Modal';

import type { FormLikeChangeEvent, RoleItem } from './types';

type AddRoleFormValues = {
  userId: number | null;
  roles: RoleItem[];
};

interface Props {
  setShowAddUserModal: (open: boolean) => void;
  setIsRoleAdded: (value: boolean) => void;
}

export const AddUserModal: React.FC<Props> = (props) => {
  const { setShowAddUserModal, setIsRoleAdded } = props;

  const [selectedRolesList, setSelectedRolesList] = useState<RoleItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | number>('');
  const [isLoading, setIsLoading] = useState(false);
  const [userFullName, setUserFullName] = useState('');

  const employeeDebounceOptions = useEmployeeDebounceOptions();
  const employeeOptions = useMemo(() => employeeDebounceOptions[1], [employeeDebounceOptions]);

  const handleSubmit = async (values: AddRoleFormValues) => {
    try {
      const { userId, roles } = values;
      if (userId === null) {
        return;
      }

      setIsLoading(true);

      await addRole(userId, roles);

      success({
        title: 'Success',
        message: `You have successfully added ${userFullName} for the role.`,
      });
      setIsRoleAdded(true);
      setShowAddUserModal(false);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserNameFromChange = (options: FormLikeChangeEvent): string => {
    const selectedValue = options.target.value;
    if (typeof selectedValue !== 'string' && typeof selectedValue !== 'number') {
      return '';
    }

    const match = employeeOptions.find(({ value }) => String(value) === String(selectedValue));
    return match?.label ? String(match.label) : '';
  };

  useEffect(() => {
    if (!selectedUser) {
      return;
    }

    const fetchUserRoles = async () => {
      try {
        const data = (await fetchRoles(selectedUser)) as { roles?: RoleItem[] };

        setSelectedRolesList(data?.roles || []);
      } catch (err) {
        handleError(err);
      }
    };

    void fetchUserRoles();
  }, [selectedUser]);

  return (
    <Modal
      isOpen={true}
      onClose={() => setShowAddUserModal(false)}
      title={{ text: 'Add User', type: 'color' }}
      className="edit-role"
      closeButton={true}
      renderFooter={false}
      htmlContent={
        <Formik<AddRoleFormValues>
          initialValues={{ userId: null, roles: [] }}
          validationSchema={addRoleSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, errors, touched, setFieldTouched, setFieldValue }) => (
            <form className="form" onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12">
                  <div className="lf-input-wrap">
                    <div className="lf-input lf-input--required p-1rem pt-0">
                      <CreateEditRole
                        setSelectedRolesList={setSelectedRolesList}
                        selectedRolesList={selectedRolesList}
                        touched={touched}
                        errors={errors}
                        employeeDebounceOptions={employeeDebounceOptions}
                        onUserBlur={() => setFieldTouched('userId')}
                        onUserSelectChange={(options) => {
                          const selectedValue = options.target.value;
                          if (typeof selectedValue !== 'string' && typeof selectedValue !== 'number') {
                            return;
                          }

                          setSelectedUser(selectedValue);
                          setUserFullName(getUserNameFromChange(options));
                          setFieldValue('userId', Number(selectedValue));
                        }}
                        handleBlur={() => setFieldTouched('roles')}
                        afterChange={() => {
                          setFieldValue('roles', selectedRolesList);
                        }}
                        selectedUser={selectedUser}
                      />

                      {touched.roles && errors.roles && (
                        <div className="error-block">{typeof errors.roles === 'string' ? errors.roles : ''}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="lf-modal__footer pb-0 pl-0">
                <button
                  className={classNames('btn btn--large btn--primary mr-16 py-8 px-12', {
                    'btn--loading': isLoading,
                  })}
                  type="submit"
                  disabled={isLoading}
                >
                  <span className="btn__label">Save & Close</span>
                  {isLoading && <Loading />}
                </button>
                <button
                  className={classNames('btn btn--large btn--outlined-grey py-8 px-12')}
                  onClick={() => setShowAddUserModal(false)}
                  disabled={isLoading}
                  type="button"
                >
                  <span className="btn__label">Cancel</span>
                </button>
              </div>
            </form>
          )}
        </Formik>
      }
    />
  );
};
